/** The Visit surfaces' model shape and the read-only and plan-lifecycle policy every Visit command and surface reads. */
import { ServicePlanStatusSystemName, VisitAgeState, VisitBrowseMode, VisitBrowseRow, VisitContentSource, visitDisplayStatus } from '@jms/model';

/** Visit host context the board hands the Detail quick view. */

export interface VisitSurfaceParams {
  servicePlanId?: string;
  /** An explicit host lock, carried with the board's other params. */
  hostReadonly?: boolean;
}

export interface VisitSurfaceContext {
  fromServicePlan: boolean;
  hostReadonly: boolean;
  /** Work left out of this Visit's Job is waiting for approval; shows the Detail's "Not in this Job" section. */
  notInJob?: boolean;
}

/** Signal forms treat missing/undefined as "no field". Keep optional lookups as null so `job` stays on the tree. */
export function visitSurfaceModel(record: VisitBrowseRow): VisitBrowseRow {

  return {
    ...record,
    // Board cards use the line name; this inspector title is the entity so the screen is not mistaken for a line.
    name: 'Visit',
    displayStatus: visitDisplayStatus(record),
    datesOverridden: record.datesOverridden ?? false,
    approvedBy: record.approvedBy ?? null,
    job: record.job ?? null,
    // Seeded whole so the Job section's nested binds stay on the tree for a Job-less Visit too.
    jobSummary: { crew: record.jobSummary?.crew ?? [], appointmentAt: record.jobSummary?.appointmentAt ?? null },
    // A read that predates the age fields is Current: never guess a Visit late.
    ageState: record.ageState ?? VisitAgeState.Current,
    lineOwner: record.lineOwner?._id ? record.lineOwner : null,
    site: record.site ?? null,
    customer: record.customer ?? null,
    servicePlan: record.servicePlan ?? null,
    serviceLine: record.serviceLine ?? null,
    contractor: record.contractor?._id ? record.contractor : null,
    resources: (record.resources ?? []).filter(resource => !!resource?._id),
  } as VisitBrowseRow;
}

/** Why each non-running plan state refuses a person's change; the server words its refusal the same. */
const PLAN_NOT_STARTED_REASONS: Partial<Record<ServicePlanStatusSystemName, string>> = {
  [ServicePlanStatusSystemName.Draft]: 'This Service Plan is a draft',
  [ServicePlanStatusSystemName.Paused]: 'This Service Plan is paused',
  [ServicePlanStatusSystemName.Archived]: 'This Service Plan is archived',
};

/**
 * Why this Visit's work may not be moved, skipped, restored, undone or reset, or undefined when it may: only a
 * Started plan's work changes. Read from `planStatus`, which the
 * browse read derives from the plan; a row without it is treated as running, as the server treats it. Every
 * Visit movement command, bulk action and surface asks this rather than checking the plan itself.
 */
export function visitPlanNotStartedReason(row: Pick<VisitBrowseRow, 'planStatus'> | null | undefined): string | undefined {

  return row?.planStatus ? PLAN_NOT_STARTED_REASONS[row.planStatus] : undefined;
}

/** Operational projections remain editable unless the enclosing host explicitly locks this Visit. */
export function visitSurfaceReadonly(record: VisitBrowseRow, hostReadonly = false): boolean {

  if (hostReadonly) return true;
  // A paused or archived plan's Visit is browseable only: no move, skip, undo, reset or edit is offered.
  if (visitPlanNotStartedReason(record)) return true;
  if (record.job?._id) return true;
  if (record.active === false) return true;
  if (record.mode === VisitBrowseMode.PlanPreview) return true;
  if (record.contentSource === VisitContentSource.Snapshot) return true;
  const status = record.status;
  return status === 'Skipped';
}
